// An isolation that is a RULE rather than a list.
//
// Isolating a whole category used to mean shipping every element id in it -
// 1,049,917 for SpaceX's Electrical Fixtures, about 84 MB as a Set of
// strings in the tab. It never needed to: the materialiser keeps ONE
// instance pool PER FRAGMENT HASH, so "is this in the isolation?" can be
// asked of the pool instead of of each element. That category is 8,130
// hashes.
//
// THE SHAPE THAT MAKES THIS ONE DOOR, not two: a plain Set already answers
// `has(uid, hash)` correctly - it ignores the second argument. So every
// existing call site keeps working untouched, a uid isolation stays a Set,
// and this class is just the other thing that can sit in `store.isolated`.
// Nothing needs to know which it is holding.
//
// TWO THINGS IT MUST GET RIGHT, both found by measuring rather than
// reasoning (root-8e, 2026-09-20):
//
//   MERGED GEOMETRY. A fragment used by exactly one element is pre-merged
//   by the exporter into one mesh per tile, and a merged mesh carries
//   element uids but NO hashes. Those elements cannot be gated by hash at
//   all, so their uids ride along in `singles`.
//
//   DOCUMENT SCOPE. A pool is shared across every tile AND every document,
//   so a hash on its own would show copies of that geometry living in files
//   the person has filtered out - isolating by hash is simply WRONG while a
//   document filter is on, not merely approximate. The fix is that the rule
//   also carries the allowed 36-character document prefixes and tests the
//   instance's own uid against them. Honest cost: with a filter on, a pool
//   spanning documents is a per-instance prefix compare rather than one
//   test per pool. It is a 36-character compare with no allocation, but it
//   is not free and should not be described as free.
export const PREFIX_LEN = 36;

export default class FragmentIsolation {
  /**
   * hashes   fragment hashes the isolated rows use
   * singles  uids of single-element fragments (the merged path)
   * prefixes allowed document prefixes, or empty for "every document"
   */
  constructor({ hashes = [], singles = [], prefixes = [], elementCount = null, excludeUids = null, within = null } = {}) {
    this.hashes = hashes instanceof Set ? hashes : new Set(hashes);
    this.singles = singles instanceof Set ? singles : new Set(singles);
    this.prefixes = prefixes && prefixes.length
      ? (prefixes instanceof Set ? prefixes : new Set(prefixes))
      : null;
    // What the rule STANDS FOR, which is not what it holds: 8,130 hashes
    // can mean a million elements, and the draw decision cares about the
    // million. The resolver sends it as `count`.
    this.elementCount = elementCount;
    // "This category EXCEPT that one." A person ticks a category and then
    // ctrl-clicks a few elements out of it (owner, 2026-09-20). Without
    // this a rule could only ever grow, so the tree would say one thing
    // and the viewer another - the disagreement is the bug class, not the
    // missing feature.
    // Named excludeUids, not "except", because they can ONLY be uids: a
    // hash is shared geometry, so dropping one element by hash would drop
    // every twin of it. root-8e's name, and the clearer one - one word on
    // the wire and in the matcher, so the two halves cannot drift.
    const dropped = excludeUids instanceof Set ? excludeUids : (excludeUids ? new Set(excludeUids) : null);
    this.excludeUids = (dropped && dropped.size) ? dropped : null;
    // THE PAGE'S CUT, when there is one (owner, 2026-10-19: "it should only
    // show elements that belong to the page's level, or ... higher or lower
    // if the section config allows"). A hash is shared by every copy of that
    // geometry on every floor, so a ticked category drawn by hash showed its
    // copies on every level. `within` is the uid list the page cut resolved
    // to (levels plus whatever the section config adds); nothing outside it
    // is drawn, whatever the rule says.
    this.within = within ? (within instanceof Set ? within : new Set(within)) : null;
    this.isFragmentIsolation = true;
  }

  // `size` exists because the controller tests it for truthiness to decide
  // whether an isolation is active (_isolationSet). A rule with hashes but
  // no singles is still an isolation, so this must never be 0 when the
  // rule holds anything at all.
  get size() { return this.hashes.size + this.singles.size; }

  _inScope(uid) {
    if (!this.prefixes) return true;
    return this.prefixes.has(String(uid).slice(0, PREFIX_LEN));
  }

  /**
   * The one question anything ever asks an isolation.
   * `hash` is the fragment hash of the pool the element lives in, when the
   * caller knows it; merged meshes and legacy call sites pass nothing and
   * are answered from `singles`.
   */
  has(uid, hash) {
    // Exclusions are tested FIRST and beat everything: a dropped element is
    // out whether it was in by hash, by singles or by nothing at all.
    if (this.excludeUids && this.excludeUids.has(uid)) return false;
    if (this.within && !this.within.has(uid)) return false;
    if (hash !== undefined && hash !== null && this.hashes.has(hash)) {
      return this._inScope(uid);
    }
    if (this.singles.has(uid)) return this._inScope(uid);
    return false;
  }

  /**
   * Whole-pool question, for admission: could this pool hold anything
   * isolated? Answered without looking at any element. When a document
   * filter is on this stays true for a pool that is only partly in scope -
   * deliberately, because admission must not drop a pool that holds
   * anything we need; the per-element test then hides the rest.
   */
  hasHash(hash) { return this.hashes.has(hash); }

  /**
   * What the selection STANDS FOR, after exclusions - the one number the
   * bulk bar, the details panel, the tree label and any message must all
   * show. Deriving it here is what stops those four drifting apart.
   */
  get effectiveCount() {
    if (this.elementCount === null || this.elementCount === undefined) return null;
    return Math.max(0, this.elementCount - (this.excludeUids ? this.excludeUids.size : 0));
  }

  /** Diagnostics only. */
  describe() {
    return {
      kind: 'fragments',
      hashes: this.hashes.size,
      singles: this.singles.size,
      documentScoped: !!this.prefixes,
      excluded: this.excludeUids ? this.excludeUids.size : 0,
      within: this.within ? this.within.size : null,
      effectiveCount: this.effectiveCount,
    };
  }
}

// ===========================================================================
// WHETHER AN ISOLATION OUTRANKS THE DRAW BUDGET
//
// Today an active isolation is THE ONLY RULE at admission: it returns
// before the size floor, the budget and the heavy-fragment check. That is
// right for what it was written for - isolating three fittings must show
// three fittings, and a floor that then refuses to draw them leaves an
// empty view, which reads as "isolate is broken". It is catastrophic for a
// category: SpaceX's Electrical Fixtures is 1,049,917 of that model's
// 1,354,126 elements, so isolating it would try to draw three-quarters of
// the model with every protection deliberately switched off.
//
// So the bypass is kept for a SMALL isolation and dropped for a large one,
// where isolation decides what is ADMITTED and the normal rules decide what
// is DRAWN. Above the threshold nothing disappears: the focus guarantee
// still loads whatever the camera is pointed at, so a person sees what they
// are looking at and distant parts stay coarse until they move closer -
// which is how the rest of the viewer already behaves.
//
// A JUDGEMENT, not a fact - change this NUMBER, not the logic around it.
// Too low and a deliberate mid-size pick (a level, a floor's worth of
// racks) starts getting floored and someone reports "isolate hides things".
// Too high and a big category takes the whole model's draw cost with the
// brakes off. If people report the first, raise it; the second, lower it.
export const SMALL_ISOLATION_ELEMENTS = 50000;

/**
 * Does this isolation get to ignore the size floor and the budget?
 * A uid Set reports its element count as `size`, so today's behaviour is
 * preserved exactly for every isolation smaller than the threshold.
 */
export function isolationOutranksBudget(isolated, threshold = SMALL_ISOLATION_ELEMENTS) {
  if (!isolated) return false;
  // After exclusions: dropping most of a category makes it a small
  // isolation again, and it should draw like one.
  const n = (isolated.effectiveCount !== undefined && isolated.effectiveCount !== null)
    ? isolated.effectiveCount
    : ((isolated.elementCount !== undefined && isolated.elementCount !== null)
      ? isolated.elementCount
      : (isolated.size || 0));
  return n > 0 && n <= threshold;
}

/** A Set already satisfies the interface; this says so in one place. */
export function isolationHas(isolated, uid, hash) {
  if (!isolated) return true;
  return isolated.has(uid, hash);
}

// ===========================================================================
// THE NORMALISER, IN ONE PLACE
//
// The page builds a rule as a PLAIN OBJECT, and that is the deliberate
// contract (selectionUids.ruleForSelection: arrays, no class, no methods -
// it is assembled from fetch responses and has to survive being one).
// Every consumer that asks it `has(uid, hash)` must turn it into a matcher
// first.
//
// This lived as a private helper inside ViewerStateStore, which fixed the
// STORE and left every other consumer exposed. It cost the owner his
// viewer twice in one day on two different paths: the setters, and then
// the FRAMING path, which never goes through the store at all - the page
// hands the rule straight to the viewer as an option, so it reached
// `rule.has()` as a plain object and threw.
//
// So it is exported HERE, beside the class it builds, and anything taking
// a rule from outside calls it. A new consumer that forgets is the same
// bug a third time; there is nothing clever left to do about that except
// keep the door in one place and make it free to walk through.
/**
 * Anything rule-shaped -> something that answers has(uid, hash), or null.
 * Idempotent, and it allocates nothing for a value that already matches.
 */
export function asFragmentRule(rule) {
  if (!rule) return null;
  // Already answers the one question. A FragmentIsolation does; so does a
  // plain Set, which answers has(uid) and ignores the hash - which is the
  // whole reason a uid isolation never needed a special case anywhere.
  if (typeof rule.has === 'function') return rule;
  // Rule-shaped, but inert.
  if (!rule.hashes && !rule.singles && !rule.excludeUids) return null;
  return new FragmentIsolation(rule);
}

/**
 * `rule` narrowed to the uids in `within` (the page's cut). The rule's own
 * fields are kept; its element count is no longer known, so it is dropped.
 */
export function narrowedTo(rule, within) {
  if (!rule || !within) return rule;
  return new FragmentIsolation({
    hashes: rule.hashes, singles: rule.singles, prefixes: rule.prefixes ? [...rule.prefixes] : [],
    elementCount: null, excludeUids: rule.excludeUids, within,
  });
}
